import { useState } from 'react'
import { TablaDatos, ayudanteColumnas } from '../../componentes/TablaDatos'
import { EstadoError } from '../../componentes/EstadoError'
import { EstadoVacio } from '../../componentes/EstadoVacio'
import { SkeletonList } from '../../componentes/Skeleton'
import { IconoCerrar } from '../../componentes/iconos'
import { BarraFiltros } from './BarraFiltros'
import { useClientes, type ClienteResumen } from '../../hooks/useClientes'
import { useFiltrosCatalogo } from '../../hooks/useFiltrosCatalogo'
import type { Filtros } from '../../hooks/useFiltrosUrl'

const ayudante = ayudanteColumnas<ClienteResumen>()
const COLUMNAS = ayudante.columns([
  ayudante.accessor('nombre', { header: 'Nombre' }),
  ayudante.accessor((fila) => fila.movil ?? '', { id: 'movil', header: 'Movil' }),
  ayudante.accessor((fila) => fila.direccion ?? '', { id: 'direccion', header: 'Direccion' }),
  ayudante.accessor((fila) => fila.ciudad ?? '', { id: 'ciudad', header: 'Poblacion' }),
  ayudante.accessor((fila) => fila.codigo_postal ?? '', { id: 'codigo_postal', header: 'C.P.' }),
])

/**
 * Modal con la tabla de clientes a pantalla casi completa: aquí sí vive todo el
 * scroll y los filtros. Se abre desde el botón "Ampliar" de la tabla delimitada
 * de Logística. Tiene su propio estado de filtros/página (independiente de la
 * URL) para no alterar la vista de fondo. Al pulsar un cliente, lo abre en el
 * Perfil (callback onElegir).
 */
export function ModalTablaClientes({
  onCerrar,
  onElegir,
}: {
  onCerrar: () => void
  onElegir: (cliente: ClienteResumen) => void
}) {
  const [filtros, setFiltros] = useState<Filtros>({ q: '', page: 1 })
  const clientes = useClientes(filtros)
  const catalogo = useFiltrosCatalogo()

  const filas = clientes.data?.items ?? []
  const total = clientes.data?.total ?? 0
  const pagina = clientes.data?.page ?? filtros.page
  const tamano = clientes.data?.page_size || 200
  const paginas = Math.max(1, Math.ceil(total / tamano))

  const cambiarFiltro = (clave: 'semana' | 'ruta' | 'q', valor: string | undefined) =>
    setFiltros((f) => ({ ...f, [clave]: clave === 'q' ? (valor ?? '') : valor, page: 1 }))
  const irAPagina = (p: number) => setFiltros((f) => ({ ...f, page: p }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative flex h-full max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-borde bg-superficie shadow-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-borde px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold text-tinta">Todos los clientes</h2>
            <p className="text-xs text-tinta-apagada">{total} en total · pulsa uno para ver su perfil</p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="flex size-9 items-center justify-center rounded-lg text-tinta-apagada transition-colors hover:bg-elevado"
          >
            <IconoCerrar className="size-5" />
          </button>
        </header>

        <div className="border-b border-borde px-5 py-3">
          <BarraFiltros
            filtros={filtros}
            semanas={catalogo.data?.semanas ?? []}
            rutas={catalogo.data?.rutas ?? []}
            onCambiar={cambiarFiltro}
          />
        </div>

        {/* Zona de scroll: aquí SÍ hay todo el scroll que quieras. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {clientes.isPending ? (
            <SkeletonList />
          ) : clientes.isError ? (
            <EstadoError error={clientes.error} onReintentar={() => void clientes.refetch()} />
          ) : filas.length === 0 ? (
            <EstadoVacio titulo="Sin resultados" descripcion="Ningún cliente coincide con los filtros." />
          ) : (
            <TablaDatos
              datos={filas}
              columnas={COLUMNAS}
              onSeleccionar={onElegir}
              seleccionadoId={null}
              estadoVacio={null}
              etiqueta="Todos los clientes"
            />
          )}
        </div>

        <nav aria-label="Paginacion" className="flex items-center justify-between gap-3 border-t border-borde px-5 py-3 text-sm">
          <button
            type="button"
            disabled={pagina <= 1}
            onClick={() => irAPagina(pagina - 1)}
            className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
          >
            Anterior
          </button>
          <span className="text-tinta-apagada">Pagina {pagina} de {paginas}</span>
          <button
            type="button"
            disabled={pagina >= paginas}
            onClick={() => irAPagina(pagina + 1)}
            className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
          >
            Siguiente
          </button>
        </nav>
      </div>
    </div>
  )
}
